import type {
  BoxProps
} from '@chakra-ui/react'
import {
  Box,
  Button,
  Grid,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { SplitButton } from 'BrokerTsLib/src/components/SplitButton'
import type {
  IInsuranceParams,
  InsuranceParamsLeasingDto,
  InsuranceParamsPropertyDto
} from 'BrokerTsLib/src/dtos'
import {
  formatPersonAddress,
  formatPersonFullName
} from 'BrokerTsLib/src/dtos'
import { Product } from 'BrokerTsLib/src/enums'
import { useEffect, useRef } from 'react'

import { InsuranceSettingsFormProperty } from '@/containers/forms/InsuranceSettingsForm/InsuranceSettingsFormProperty'
import { InsuranceSettingsFormTravel } from '@/containers/forms/InsuranceSettingsForm/InsuranceSettingsFormTravel'
import { useSendCalculationContext } from '@/containers/steps/SendCalculationModal'
import { useAuth } from '@/context/AuthContext'
import { useOffersAddonContext } from '@/context/OffersAddonProvider'
import { offersPropertyContext } from '@/context/OffersPropertyProvider'
import { offersTravelContext } from '@/context/OffersTravelProvider'
import { offersVehicleContext } from '@/context/OffersVehicleProvider'
import type { IPrintContext } from '@/context/PrintContext'
import { usePrintContext } from '@/context/PrintContext'
import { useOffersContext } from '@/context/useOffersContext'
import { Apps } from '@/dtos'
import { useApp } from '@/hooks/useApp'
import { GetAppPath } from '@/utils/appUtils'

import { useGetApiLogs } from '@/hooks/useDocuments'
import { formatDateTime } from 'BrokerTsLib/src/Utils/formatPrice'
import { Link as RRLink } from 'react-router-dom'
import { Apis } from '../../appSettings/AppSettings'
import { Container } from '../../components/ui/Container'
import { InsuranceSettingsForm as InsuranceSettingsFormVehicle } from '../../containers/forms/InsuranceSettingsForm'
import { useInsuranceParams } from '../../context/InsuranceParamsProvider'
import { usePartner } from '../../context/PartnerContext'
import { useSidebar } from '../../context/SidebarContext'
import { useStepsContext } from '../../context/StepsContext'
import { useWizzardHeader } from '../../context/WizzardHeaderContext'
import { useUrl } from '../../hooks'
import { throttle } from '../../utils/throttle'
import { ConfirmModal } from '../ConfirmModal'
import AddInsuranceAdditionalDates from './AddInsuranceAdditionalDates'
import AddVehicleInfo from './AddVehicleInfo'
import { WizzardHeaderButton } from './WizzardHeaderButton'
import { WizzardHeaderButtons } from './WizzardHeaderButtons'
import { WizzardHeaderContent } from './WizzardHeaderContent'
import { WizzardHeaderContentWrapper } from './WizzardHeaderContentWrapper'
import { WizzardHeaderHistory } from './WizzardHeaderHistory'
import { WizzardHeaderPropertyInfo } from './WizzardHeaderPropertyInfo'
import { WizzardHeaderTitle } from './WizzardHeaderTitle'
import { WizzardHeaderTravelInfo } from './WizzardHeaderTravelInfo'
import { WizzardHeaderVehicleInfo } from './WizzardHeaderVehicleInfo'
import { WizzardHeaderWrapper } from './WizzardHeaderWrapper'

export interface WizzardHeaderProps extends BoxProps {
  isScrolled?: boolean
  onCreateNote?: () => void
}

export const getOffersContext = (app: Apps) => {
  switch (app) {
    case Apps.leasing:
    case Apps.leasingSimplifiedWizard:
    case Apps.extra:
    case Apps.fleet:
    case Apps.vehicle:
    case Apps.publicPoleas:
      return offersVehicleContext
    case Apps.travel:
      return offersTravelContext
    case Apps.property:
      return offersPropertyContext
  }
}

export const WizzardHeader = ({
  onCreateNote,
  sx,
  ...rest
}: WizzardHeaderProps) => {
  const { product, app } = useApp()
  const isPublicPoleas = app == Apps.publicPoleas
  const { user } = useAuth()
  const { printMode, type, setPrintMode, setType } = usePrintContext() ?? {}
  const { getBaseUrl, currentPartner } = usePartner()

  const {
    isOpen: isOpenCopyAsExtraConfirm,
    onClose: oncCloseCopyAsExtraConfirm,
    onOpen: onOpenCopyAsExtraConfirm,
  } = useDisclosure()

  const context = useOffersContext()

  const {
    insuranceParams: ip,
    insuranceParamsId,
    isContracted,
  } = useInsuranceParams()!
  const insuranceParams = ip as IInsuranceParams
  const ipProperty = ip as InsuranceParamsPropertyDto

  const { currentStepId } = useStepsContext()
  const { offers, updateOffer, sortBy } = context
  const { offers: addonOffers, updateOffer: updateAddonOffers } =
    useOffersAddonContext()

  const activeAddonOffers = addonOffers.filter((x) => x.isActive) ?? []

  const insuranceParamsUrl = useUrl(Apis.insuranceParams)

  const stickyOffset = 8
  const whiteGap = printMode ? 0 : 4
  const threshold = 76 + (printMode ? 0 : 56) - stickyOffset // header height + steps height - offset
  const { setSidebarTopOffset } = useSidebar()
  const { isScrolled, setIsScrolled, setHeaderHeight, headerHeight } = useWizzardHeader()

  const handleSetPrintMode = (
    printMode: boolean,
    type?: IPrintContext['type'],
  ) => {
    setPrintMode(printMode)
    setType(type ?? null)

    offers
      .filter((x) => !printMode || x.included)
      .forEach((o) => {
        o.print = printMode
        updateOffer(o)
      })

    activeAddonOffers
      .filter(
        (x) =>
          !printMode ||
          (x.included &&
            x.isActive &&
            (ip?.baseParams?.addonVariants?.filter(
              (v) => v.riskVariantId == x.riskVariantId && v.selected,
            ).length ?? 0) > 0),
      )
      .forEach((o) => {
        o.print = printMode
        updateAddonOffers(o)
      })
  }

  const headerRef = useRef(null)
  useEffect(() => {
    const headerElement = headerRef?.current

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeaderHeight?.(entry.borderBoxSize[0].blockSize)
      }
    })

    if (headerElement) resizeObserver.observe(headerElement)

    return () => {
      if (headerElement) resizeObserver.unobserve(headerElement)
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setSidebarTopOffset?.(headerHeight ?? 0)

      // if (window.scrollY > headerHeight) {
      if (window.scrollY > threshold) {
        setIsScrolled(true)
      } else {
        setIsScrolled(false)
      }
    }

    window.addEventListener('scroll', throttle(handleScroll))

    return () => {
      window.removeEventListener('scroll', throttle(handleScroll))
    }
  }, [headerHeight, setIsScrolled, setSidebarTopOffset, threshold])

  const cloneReturnUrl = encodeURIComponent(
    `${getBaseUrl()}/${GetAppPath(app)}steps/1`,
  )
  const cloneToExtraWizardReturnUrl = encodeURIComponent(
    `${getBaseUrl()}/${GetAppPath(app)}extra/steps/1`,
  )
  const cloneUrl = `${insuranceParamsUrl}/CloneUrl?paramsId=${insuranceParamsId}&replacement=${false}&returnUrl=${cloneReturnUrl}`
  const cloneToExtraWizardUrl = `${insuranceParamsUrl}/CloneToExtraUrl?paramsId=${insuranceParamsId}&returnUrl=${cloneToExtraWizardReturnUrl}`

  const selectedOffertsToPrint = [...offers, ...activeAddonOffers].filter(
    (x) =>
      x.print &&
      x.errors.filter((y) => !y.softError).length == 0 &&
      x.price > 0,
  )
  const toPrintCount = selectedOffertsToPrint.length

  const printOffersIdsQuery = selectedOffertsToPrint
    .map((o) => o.offerId)
    .join(',')

  const documentUrl = useUrl(Apis.document)
  const tiskUrl = `${documentUrl}/GetOffersPdf?paramsId=${insuranceParamsId ?? ''}&insurerOfferIds=${printOffersIdsQuery}`

  let person = insuranceParams?.policyHolder.person
  if (product == Product.VehicleLeasingInsurance)
    person = (insuranceParams as InsuranceParamsLeasingDto)?.client ?? person

  const { data: allApiLogs } = useGetApiLogs(insuranceParamsId!, { retry: false, enabled: !isPublicPoleas })
  const apiLogs = allApiLogs?.data.filter(
    (x) => (x.insurerOfferId?.length ?? 0) <= 0
  )

  return (
    <Box as="header" sx={{
      h: { xl: '225px' }
    }}>
      <Box
        ref={headerRef}
        id="wizzard-header"
        sx={{
          position: { base: 'static', md: isScrolled ? 'fixed' : 'relative' },
          top: isScrolled && !printMode ? `${stickyOffset + whiteGap}px` : 0,
          right: 0,
          left: 0,
          py: 4,
          bg: 'wizzard-header-bg',
          // transition: 'all 200ms ease-in-out',
          zIndex: isScrolled ? '1001' : 'sticky', // zmenseno z 2001 na 1001 aby byl videl modal a overlay v cestovku (doplnky offery)
          _before: {
            content: '""',
            position: 'absolute',
            top: `-${whiteGap}px`,
            left: 0,
            right: 0,
            height: `${whiteGap}px`,
            bg: 'wizzard-header-gap-bg',
          },
          ...sx,
        }}
        {...rest}
      >
        <Container>
          <WizzardHeaderWrapper>
            {[
              Product.VehicleInsurance,
              Product.FleetInsurance,
              Product.VehicleLeasingInsurance,
            ].includes(product!) && <WizzardHeaderVehicleInfo />}

            {[Product.TravelInsurance].includes(product!) && !printMode && (
              <WizzardHeaderTravelInfo />
            )}

            {[Product.PropertyInsurance, Product.LiabilityInsurance].includes(product!) && !printMode && (
              <WizzardHeaderPropertyInfo />
            )}

            <WizzardHeaderContentWrapper
              sx={{ alignContent: printMode ? 'center' : undefined }}
            >
              <WizzardHeaderTitle>
                {isContracted &&
                  !printMode &&
                  insuranceParams?.generalParams.contractNumber
                  ? `Pojistná smlouva č. ${insuranceParams?.generalParams.contractNumber}, `
                  : null}

                {!isContracted &&
                  !printMode &&
                  app != Apps.publicPoleas &&
                  `Nabídka pojištění č. ${insuranceParams?.generalParams.paramsId}, `}

                {!isContracted &&
                  printMode &&
                  type == 'print' &&
                  `Tisk nabídky pojištění č. ${insuranceParams?.generalParams.paramsId}, `}

                {!isContracted &&
                  printMode &&
                  type == 'email' &&
                  `Odeslání nabídky pojištění č. ${insuranceParams?.generalParams.paramsId}, `}

                <strong>
                  {formatPersonFullName(person)},{' '}
                  {formatPersonAddress(product == Product.PropertyInsurance
                    ? ipProperty.property.data.address
                    : person?.address
                  )}
                </strong>
              </WizzardHeaderTitle>

              <WizzardHeaderContent>
                <Grid
                  sx={{
                    gap: { base: 4, lg: 12 },
                    alignItems: -isScrolled ? 'center' : 'start',
                    gridAutoFlow: {
                      base: 'row',
                      md: isScrolled ? 'column' : 'row',
                    },
                  }}
                >
                  {printMode && (
                    <WizzardHeaderButtons
                      sx={{
                        alignItems: 'center',
                        gridTemplateColumns: '150px 150px',
                      }}
                    >
                      <WizzardHeaderButton
                        onClick={() => {
                          handleSetPrintMode(false, null)
                        }}
                      >
                        Zpět
                      </WizzardHeaderButton>

                      {type == 'print' && (
                        <Link
                          as={'a'}
                          target="_blank"
                          variant="noDecoration"
                          href={tiskUrl}
                          display="flex"
                        >
                          <WizzardHeaderButton
                            isDisabled={toPrintCount == 0}
                            colorScheme="green"
                          >
                            Tisk {toPrintCount > 0 && `(${toPrintCount})`}
                          </WizzardHeaderButton>
                        </Link>
                      )}

                      {type == 'email' && (
                        <SendEmailButton toPrintCount={toPrintCount} />
                      )}
                    </WizzardHeaderButtons>
                  )}

                  {!printMode && (
                    <>
                      {[
                        Product.VehicleInsurance,
                        Product.FleetInsurance,
                        Product.VehicleLeasingInsurance,
                      ].includes(product!) &&
                        <InsuranceSettingsFormVehicle />
                      }

                      {product == Product.TravelInsurance &&
                        <InsuranceSettingsFormTravel />
                      }

                      {[Product.PropertyInsurance, Product.LiabilityInsurance].includes(product!) &&
                        <InsuranceSettingsFormProperty />
                      }

                      <WizzardHeaderButtons>
                        {!isContracted &&
                          currentStepId == 2 &&
                          product != Product.VehicleLeasingInsurance &&
                          !!setPrintMode &&
                          ((offers.filter((x) => x.included && x.price > 0 && x.errors.every((y) => y.softError)).length ?? 0) > 0 ||
                            activeAddonOffers.length > 0 ||
                            currentStepId! > 2
                            ? <WizzardHeaderButton onClick={() => { handleSetPrintMode(true, 'email') }}>E-mail</WizzardHeaderButton>
                            : <WizzardHeaderButton isDisabled>E-mail</WizzardHeaderButton>
                          )}

                        {!isContracted &&
                          currentStepId == 2 &&
                          !!setPrintMode &&
                          ((offers.filter((x) => x.price > 0 && x.errors.every((y) => y.softError)).length ?? 0) > 0 || 
                            activeAddonOffers.length > 0 || 
                            currentStepId! > 2
                            ? <WizzardHeaderButton onClick={() => { handleSetPrintMode(true, 'print') }}>Tisk</WizzardHeaderButton>
                            : <WizzardHeaderButton isDisabled>Tisk</WizzardHeaderButton>
                          )}

                        {apiLogs &&
                          apiLogs.length > 0 &&
                          user?.enableOfferXml &&
                          !printMode &&
                            <Menu size="sm" placement="bottom" >
                              <MenuButton
                                sx={{ gap: 2, whiteSpace: 'nowrap', color: 'white' }}
                                type="button"
                              >
                                ApiLog
                              </MenuButton>
                              <MenuList>
                                {apiLogs.map((doc) => (
                                  <MenuItem
                                    key={doc.id}
                                    as={RRLink}
                                    to={`${getBaseUrl()}/apilog/${doc.id}`}
                                  >
                                    {doc.action} (
                                    {formatDateTime(
                                      new Date(doc.requestUtc),
                                      'D.M.YYYY HH:mm:ss.SSS',
                                    )}
                                    ) - {doc.statusCode}
                                  </MenuItem>
                                ))}
                              </MenuList>
                            </Menu>
                        }

                        {isContracted && app == Apps.vehicle &&
                          <AddVehicleInfo />
                        }

                        {isContracted &&
                          user?.isSupervisor &&
                          currentPartner?.partner == 'Jap' &&
                          <AddInsuranceAdditionalDates />
                        }

                        {product != Product.VehicleLeasingInsurance &&
                          app != Apps.publicPoleas &&
                          <SplitButton
                            buttonProps={{
                              as: 'a',
                              href: cloneUrl,
                              children: 'Klonovat',
                              target: '_blank',
                              variant: 'noDecoration',
                              display: 'flex',
                              backgroundColor: 'primary.500',
                              color: 'white',
                            }}
                            splitterColor="primary.600"
                            menuItems={
                              app == Apps.vehicle ? [(
                                <>
                                  <Button
                                    onClick={() => onOpenCopyAsExtraConfirm()}
                                  >
                                    Klonovat do pojištění doplňků
                                  </Button>

                                  <ConfirmModal
                                    title="Upozornění"
                                    isOpen={isOpenCopyAsExtraConfirm}
                                    onClose={oncCloseCopyAsExtraConfirm}
                                    yesButton={
                                      <Button
                                        as={'a'}
                                        target="_blank"
                                        colorScheme="green"
                                        href={cloneToExtraWizardUrl}
                                        onClick={oncCloseCopyAsExtraConfirm}
                                      >
                                        Přejít do pojištění doplňků
                                      </Button>
                                    }
                                    noButtonContent="Ne"
                                  >
                                    <Stack gap={6}>
                                      <Text>
                                        Zkopíruje základní údaje o klientovi,
                                        vozidle a založí nabídku jako pojištění
                                        doplňků.
                                      </Text>
                                      <Text>
                                        Skutečně chcete přejít do pojištění
                                        doplňků s předvyplněnými údaji podle této
                                        nabídky?
                                      </Text>
                                    </Stack>
                                  </ConfirmModal>
                                </>
                              )] : undefined
                            }
                          />
                        }
                      </WizzardHeaderButtons>
                    </>
                  )}
                </Grid>

                {!isScrolled && !printMode && <WizzardHeaderHistory />}

              </WizzardHeaderContent>
            </WizzardHeaderContentWrapper>
          </WizzardHeaderWrapper>
        </Container>
      </Box>
    </Box>
  )
}

const SendEmailButton = ({ toPrintCount }: { toPrintCount: number }) => {
  const { send: onSendCalculation, isSending } = useSendCalculationContext()

  return <WizzardHeaderButton
    onClick={onSendCalculation}
    isDisabled={toPrintCount == 0}
    colorScheme="green"
    isLoading={isSending}
  >
    Odeslat {toPrintCount > 0 && `(${toPrintCount})`}
  </WizzardHeaderButton>
}